import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Settings,
  Bell,
  MessageSquare,
  LogOut,
  ChevronDown,
  Info
} from "lucide-react";
import redPropellerIcon from "@assets/propeller-logo.png";
import { VERSION } from "@/config/version";

// Home Navigation Component
function HomeNavigationButton() {
  const [, navigate] = useLocation();
  
  const handleHomeClick = () => {
    // Only clear navigation-related session storage items
    // DO NOT clear 'user' or authentication-related items
    sessionStorage.removeItem('currentPhase');
    sessionStorage.removeItem('navigateToStep2');
    sessionStorage.removeItem('immediateStep2Navigation');
    sessionStorage.removeItem('loginSuccess');
    sessionStorage.removeItem('returningUserSignIn');
    sessionStorage.removeItem('forceStep1Portal');
    navigate('/');
  };
  
  return (
    <button
      onClick={handleHomeClick}
      className="p-3 min-h-[44px] min-w-[44px] hover:bg-red-50 rounded-lg transition-all duration-200 group touch-manipulation flex items-center justify-center"
      title="Return to Home"
    >
      <img 
        src={redPropellerIcon} 
        alt="Home" 
        className="w-5 h-5 group-hover:rotate-12 transition-transform duration-200" 
      />
    </button>
  );
}

export default function TopBanner() {
  const [notificationCount] = useState(3);
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const queryClient = useQueryClient();


  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 md:pl-68">
        {/* Left side - Propeller logo */}
        <div className="flex items-center">
          <HomeNavigationButton />
        </div>
        
        {/* Right side - User menu or Auth buttons */}
        <div className="flex items-center space-x-4">
          {!isAuthenticated && !isLoading ? (
            // Show Login/Register buttons for unauthenticated users
            <>
              <Link href="/login">
                <Button variant="ghost" size="sm" className="text-rh-helicopter-red hover:bg-rh-helicopter-red/10">
                  Sign In
                </Button>
              </Link>
              <Link href="/register">
                <Button size="sm" className="bg-rh-helicopter-red hover:bg-rh-helicopter-red/90 text-white">
                  Register
                </Button>
              </Link>
            </>
          ) : isAuthenticated ? (
            // Show authenticated user menu
            <>
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative opacity-50 cursor-not-allowed touch-manipulation" disabled>
                <Bell className="h-5 w-5 text-gray-400" />
                {notificationCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs opacity-50"
                  >
                    {notificationCount}
                  </Badge>
                )}
              </Button>
              
              {/* Communications */}
              <Button variant="ghost" size="icon" className="opacity-50 cursor-not-allowed touch-manipulation" disabled>
                <MessageSquare className="h-5 w-5 text-gray-400" />
              </Button>
              
              {/* User Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2 hover:bg-gray-100 min-h-[44px] touch-manipulation">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src="" alt="User" />
                      <AvatarFallback className="bg-rh-trust-blue text-white">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:flex flex-col items-start">
                      <span className="text-sm font-medium">{user?.firstName} {user?.lastName}</span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <div className="flex items-center space-x-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src="" alt="User" />
                        <AvatarFallback className="bg-rh-trust-blue text-white text-xs">
                          {user?.firstName?.[0]}{user?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{user?.firstName} {user?.lastName}</span>
                        <span className="text-xs text-gray-500">{user?.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Platform Version</span>
                      <span className="text-xs text-gray-500 font-mono">{VERSION}</span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <Link href="/profile">
                    <DropdownMenuItem className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </DropdownMenuItem>
                  </Link>
                  <Link href="/settings">
                    <DropdownMenuItem className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </DropdownMenuItem>
                  </Link>
                  <DropdownMenuItem className="opacity-50 cursor-not-allowed" disabled>
                    <MessageSquare className="mr-2 h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Communications</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="opacity-50 cursor-not-allowed" disabled>
                    <Bell className="mr-2 h-4 w-4 text-gray-400" />
                    <span className="text-gray-400">Notifications</span>
                    {notificationCount > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs opacity-50">
                        {notificationCount}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-red-600 focus:text-red-600"
                    onClick={() => {
                      console.log('TopBanner logout clicked');
                      // Clear cache and storage before logout
                      queryClient.clear();
                      sessionStorage.clear();
                      localStorage.clear();
                      // Use the logout method from useAuth hook
                      logout();
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}